'use client';

import { useMemo } from 'react';
import styled from 'styled-components';
import { useGetSharesForProjectNavigationQuery } from '@frameio/graphql-client/generated';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { DEFAULT_INITIAL_PAGE_SIZE } from '@frameio/utils';
import { Text, theme, useIsListBoxItemCurrentTabStop } from '@frameio/vapor';
import { IconList } from '@frameio/vapor-icons';
import { useProjectNavigationActivation } from '../ProjectNavActivationManager';
import TEXT from './generated/TEXT/AllSharesNavItem';
import {
  ShareNavItemContent,
  ShareSkeletonThumbnail,
  ThumbnailWrapper,
} from './Shares.styles';

export function AllSharesNavItem() {
  const projectId = useActiveProjectId();
  const { data: cachedSharesData } = useGetSharesForProjectNavigationQuery({
    variables: {
      projectId: projectId ?? '',
      page: { first: DEFAULT_INITIAL_PAGE_SIZE },
    },
    skip: !projectId,
  });

  const totalCount =
    cachedSharesData?.account?.project?.shares?.totalCount ?? 0;
  const { labelAllShares } = TEXT;

  const label = useMemo(
    () =>
      totalCount > 0 ? `${labelAllShares} (${totalCount})` : labelAllShares,
    [totalCount, labelAllShares]
  );

  const isActiveState =
    useProjectNavigationActivation().state === 'all-shares:all-shares';
  const isCurrentTabStop = useIsListBoxItemCurrentTabStop();

  const thumbnail = (
    <ThumbnailWrapper>
      <ShareSkeletonThumbnail
        borderColor={theme.colors.border.primary}
        icon={<IconList color={theme.colors.icon.primary} size={16} />}
        thumbnailCount={3}
      />
    </ThumbnailWrapper>
  );

  return (
    <ShareNavItemContent
      $isActive={isActiveState}
      $isFocused={isCurrentTabStop}
      adornmentBefore={thumbnail}
      label={
        <StyledLabel fontWeight="regular" truncate={true} variant="body">
          {label}
        </StyledLabel>
      }
    />
  );
}

const StyledLabel = styled(Text)`
  font-size: ${theme.fontSizes[70]};
`;

TEXT.DEFINE({
  /** Label for the all shares item, including count when available */
  labelAllShares: 'All Share Links',
});
